import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CentraJS } from "@trydig/centra-js-client";
import axios from "axios";
import Cookies from "js-cookie";
import { useRouter, useSearchParams } from "next/navigation";
import { ofetch } from "ofetch";
import { useCallback, useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ShowroomAccount } from "~/lib/showroom/types";
import { usePersistedStore } from "~/lib/stateManagement/persistedState/persistedStore";

export const getShowroomSelection = async (token: string, { signal }: { signal: AbortSignal }) => {
	const api = "/api/showroom/get-account";
	const data = await ofetch<ShowroomAccount>(api, {
		headers: {
			"Content-Type": "application/json",
			"API-Token": token || "",
		},
	});

	if (data === null) throw Error("Error getting selection");

	Cookies.set("showroomToken", data.token, {
		expires: 365,
		secure: true,
		sameSite: "strict",
	});
	return data;
};

interface ShowroomBuyer {
	buyerId: number | null;
	setBuyerId: (buyerId: number | null) => void;
}
export const useShowroomBuyer = create(
	persist<ShowroomBuyer>(
		(set) => ({
			buyerId: null,
			setBuyerId: (buyerId) => set({ buyerId }),
		}),
		{
			name: "showroom-buyer",
			skipHydration: true,
		},
	),
);

export const useShowroom = () => {
	useEffect(() => {
		if (!useShowroomBuyer.persist.hasHydrated()) {
			useShowroomBuyer.persist.rehydrate();
		}
	}, []);

	const customerToken = usePersistedStore((state) => state.showroomToken);
	const setToken = usePersistedStore((state) => state.setShowroomToken);
	const selection = useQuery({
		queryKey: ["showroom-selection"],
		queryFn: ({ signal }) => getShowroomSelection(customerToken, { signal }),
		enabled: !!customerToken,
	});

	useEffect(() => {
		if (selection.isSuccess) {
			setToken(selection.data.token);
			const firstBuyer = selection.data?.buyers?.[0];
			if (firstBuyer) {
				useShowroomBuyer.setState({ buyerId: Number(firstBuyer.id) });
			}
		}
	}, [selection.isSuccess, selection.data, setToken]);

	return {
		isLoading: selection.isLoading,
		data: selection.data,
		user: selection.data?.user,
		token: selection.data?.token,
	};
};

const loginUser = async (
	variables: { email: string; password: string },
	customerToken: string,
	setToken: (token: string) => void,
) => {
	const apiUrl = "/api/showroom/login";

	const { email, password } = variables;
	const res = await axios.post(
		`${apiUrl}`,
		{
			email,
			password,
		},
		{ headers: { "API-token": customerToken || "" } },
	);

	setToken(res.data.token);

	Cookies.set("asset-token", res.data.jwt, {
		expires: 365,
		secure: true,
		sameSite: "strict",
	});

	return res.data;
};

export const useLogoutShowroomUser = () => {
	const queryClient = useQueryClient();
	const setShowroomToken = usePersistedStore((state) => state.setShowroomToken);
	const { refresh } = useRouter();

	const logout = useCallback(() => {
		Cookies.remove("showroom-token");
		Cookies.remove("asset-token");
		setShowroomToken("");

		queryClient.cancelQueries({
			queryKey: ["showroom-selection"],
		});
		queryClient.removeQueries({
			queryKey: ["showroom-selection"],
		});

		useShowroomBuyer.setState({ buyerId: null });

		refresh();
	}, [setShowroomToken, refresh, queryClient]);

	return {
		logout,
	};
};

export const useLoginShowroomUser = () => {
	const queryClient = useQueryClient();
	const customerToken = usePersistedStore((state) => state.showroomToken);
	const setShowroomToken = usePersistedStore((state) => state.setShowroomToken);
	const router = useRouter();
	const searchParams = useSearchParams();
	const redirect = searchParams.get("redirect");

	const login = useMutation({
		mutationFn: (variables: { email: string; password: string }) =>
			loginUser(variables, customerToken, setShowroomToken),
		onMutate: () => {
			CentraJS.suspend();
		},
		onError: async (error: Error) => {
			return error;
		},
		onSuccess: async (data) => {
			queryClient.setQueryData(["showroom-selection"], data);

			Cookies.set("showroom-token", data.token, {
				expires: 365,
				secure: true,
				sameSite: "strict",
			});

			router.refresh();
			if (redirect) {
				router.push(redirect);
				return;
			}
		},
		onSettled: () => {
			CentraJS.resume();
		},
	});

	return { login };
};
